import React, { useEffect, useRef, useState } from "react";
import Modal from "react-modal";
import api from "../services/api";
import moment from "moment";
import "moment/locale/de";
import { FiCopy, FiX, FiPhone, FiMessageCircle, FiTrash2, FiCheck, FiArrowUpRight, FiLock, FiChevronDown, FiImage, FiMoreHorizontal } from "react-icons/fi";
import AppointmentImageManager from "./AppointmentImageManager";
import "./EditAppointmentDetails.css";

const editableKeys = ['clientName', 'phone', 'time', 'description', 'tattoo_category', 'minRange', 'maxRange', 'deposit', 'duration', 'project_session_note'];
const fingerprint = data => JSON.stringify(editableKeys.map(key => String(data?.[key] ?? '')));
const Field = ({ label, children }) => <label className="ed-field"><span>{label}</span>{children}</label>;
const syncViewport = node => {
  if (!node) return;
  const viewport = window.visualViewport;
  node.style.setProperty('--ed-height', `${viewport?.height || window.innerHeight}px`);
  node.style.setProperty('--ed-top', `${viewport?.offsetTop || 0}px`);
};

export default function EditAppointmentModal({
  isOpen, onClose, onSubmit, appointmentData, setAppointmentData,
  selectedAppointment, onCopyAppointment, onDeleteAppointment,
  categories = [], onOpenImages, onImagesChanged, onOpenProject,
}) {
  const [pricesOpen, setPricesOpen] = useState(true);
  const [actionsOpen, setActionsOpen] = useState(false);
  const [initial, setInitial] = useState('');
  const [customerSaving, setCustomerSaving] = useState(false);
  const [saveError, setSaveError] = useState('');
  const customerBaseline = useRef({ clientName: '', phone: '' });
  const submitLock = useRef(false);
  const wasOpen = useRef(false);
  const appointmentId = useRef(null);
  useEffect(() => {
    if (isOpen && (!wasOpen.current || appointmentId.current !== selectedAppointment?.id)) {
      setInitial(fingerprint(appointmentData));
      customerBaseline.current = { clientName: String(appointmentData?.clientName ?? ''), phone: String(appointmentData?.phone ?? '') };
      setSaveError('');
      setPricesOpen(true);
      setActionsOpen(false);
    }
    wasOpen.current = isOpen;
    appointmentId.current = selectedAppointment?.id;
  }, [isOpen, selectedAppointment?.id, appointmentData]);
  const overlay = useRef(null);
  const scroll = useRef(null);
  useEffect(() => {
    if (!isOpen) return undefined;
    const sync = () => syncViewport(overlay.current);
    sync();
    window.addEventListener('resize', sync);
    window.visualViewport?.addEventListener('resize', sync);
    window.visualViewport?.addEventListener('scroll', sync);
    return () => {
      window.removeEventListener('resize', sync);
      window.visualViewport?.removeEventListener('resize', sync);
      window.visualViewport?.removeEventListener('scroll', sync);
    };
  }, [isOpen]);
  useEffect(() => {
    if (isOpen) scroll.current?.scrollTo(0, 0);
  }, [isOpen, selectedAppointment?.id]);

  const data = appointmentData || {};
  const project = Boolean(data.project_id);
  const update = (key, value) => setAppointmentData(previous => ({ ...previous, [key]: value }));
  const time = data.time ? moment(data.time, 'HH:mm:ss').format('HH:mm') : '';
  const duration = Number(data.duration);
  const validDuration = Number.isFinite(duration) && duration > 0;
  const endTime = time && validDuration ? moment(time, 'HH:mm').add(duration, 'minutes').format('HH:mm') : '';
  const nextDay = time && validDuration && moment(time, 'HH:mm').add(duration, 'minutes').isAfter(moment(time, 'HH:mm'), 'day');
  const day = data.day ? moment(data.day).locale('de') : null;
  const phone = String(data.phone || '').replace(/[^\d+]/g, '');
  const whatsapp = phone.replace(/^00/, '').replace(/^0/, '49').replace(/\D/g, '');
  const busy = Boolean(data.isSaving) || customerSaving;
  const value = key => data[key] ?? '';
  const dirty = initial !== '' && fingerprint(data) !== initial;
  const allowLeave = () => !dirty || window.confirm('Änderungen verwerfen und schließen?');
  const close = () => { if (!busy && allowLeave()) onClose(); };

  const submit = async event => {
    event.preventDefault();
    event.persist?.();
    if (submitLock.current || busy) return;
    submitLock.current = true;
    setCustomerSaving(true);
    setSaveError('');
    try {
      const baseline = customerBaseline.current;
      const clientName = String(data.clientName ?? '');
      const phone = String(data.phone ?? '');
      if (project && (clientName !== baseline.clientName || phone !== baseline.phone)) {
        await api.put(`/api/tattoo-projects/${data.project_id}/customer`, {
          clientName, phone,
          expectedClientName: baseline.clientName, expectedPhone: baseline.phone,
        });
        customerBaseline.current = { clientName, phone };
      }
      await onSubmit(event);
    } catch (error) {
      setSaveError(error.response?.data?.message || 'Speichern fehlgeschlagen. Bitte erneut versuchen.');
    } finally {
      submitLock.current = false;
      setCustomerSaving(false);
    }
  };

  return <Modal isOpen={isOpen} onRequestClose={close}
    shouldCloseOnOverlayClick={!busy} shouldCloseOnEsc={!busy}
    contentLabel="Termindetails bearbeiten" className="ed-dialog"
    overlayClassName="ed-overlay" overlayRef={node => { overlay.current = node; syncViewport(node); }}>
    <header className="ed-header">
      <div className="ed-heading"><span className="ed-eyebrow">TERMINDETAILS</span>
        <h2>{data.clientName || 'Termin bearbeiten'}</h2>
        <p>{day?.isValid() ? day.format('ddd, DD. MMM YYYY') : 'Termin bearbeiten'}{time && ` · ${time} Uhr`}</p>
      </div>
      <div className="ed-header-actions">
        {selectedAppointment && onCopyAppointment && <button type="button" className="ed-icon" title="Termin kopieren" aria-label="Termin kopieren" disabled={busy} onClick={() => { if (allowLeave()) onCopyAppointment(selectedAppointment); }}><FiCopy /></button>}
        <button type="button" className="ed-icon" title="Schließen" aria-label="Terminfenster schließen" disabled={busy} onClick={close}><FiX /></button>
      </div>
    </header>
    <form className="ed-form" onSubmit={submit}>
      <div className="ed-scroll" ref={scroll}>
        <div className="ed-grid">
          <div className="ed-column">
          <section className="ed-section ed-customer"><h3>Kunde</h3>
            <Field label="Name"><input disabled={busy} type="text" value={value('clientName')} onChange={e => update('clientName', e.target.value)} required /></Field>
            <Field label="Telefonnummer"><input disabled={busy} type="tel" inputMode="tel" autoComplete="tel" spellCheck={false} value={value('phone')} onChange={e => update('phone', e.target.value)} placeholder="Optional" /></Field>
            {whatsapp.length >= 7 && <div className="ed-contact"><a href={`tel:${phone}`}><FiPhone />Anrufen</a><a href={`https://wa.me/${whatsapp}`} target="_blank" rel="noopener noreferrer"><FiMessageCircle />WhatsApp</a></div>}
            {project && <p className="ed-hint">Änderungen an Name und Telefonnummer gelten für alle Sessions dieses Projekts.</p>}
          </section>
          <section className="ed-section ed-schedule"><h3>Zeit & Dauer</h3>
            <div className="ed-pair">
              <Field label="Beginn"><input disabled={busy} type="time" value={time} required onChange={e => update('time', e.target.value)} /></Field>
              <Field label="Dauer · Min."><input disabled={busy} type="number" inputMode="numeric" min="1" step="1" value={value('duration')} required onChange={e => update('duration', e.target.value)} /></Field>
            </div>
            {validDuration && <p className="ed-time-summary">{Math.floor(duration / 60) > 0 && `${Math.floor(duration / 60)} Std.`}{duration % 60 > 0 && ` ${duration % 60} Min.`}{endTime && <span>Ende {endTime}{nextDay ? ' (+1 Tag)' : ''}</span>}</p>}
          </section>
          <section className="ed-section ed-motif"><h3>Motiv & Details</h3>
            {onOpenProject && selectedAppointment && <button type="button" className="ed-project" disabled={busy} onClick={() => { if (allowLeave()) onOpenProject(selectedAppointment); }}><span>{project ? data.project_title || 'Projekt öffnen' : 'Als Projekt zusammenfassen'}</span><FiArrowUpRight /></button>}
            {project && <p className="ed-hint">Die Beschreibung gilt für alle Sessions. Änderungen bitte im Projekt vornehmen.</p>}
            <Field label="Motiv & Körperstelle"><textarea disabled={busy} rows="4" value={value('description')} readOnly={project} onChange={e => update('description', e.target.value)} placeholder="Motiv, Körperstelle und wichtige Wünsche …" /></Field>
            {project && <Field label="Notiz für diese Session"><textarea disabled={busy} rows="2" value={value('project_session_note')} onChange={e => update('project_session_note', e.target.value)} placeholder="Was ist bei diesem Termin geplant?" /></Field>}
            <Field label="Tattoo-Stil"><select disabled={busy} value={value('tattoo_category')} onChange={e => update('tattoo_category', e.target.value)}><option value="">Keine Kategorie</option>{value('tattoo_category') && !categories.includes(data.tattoo_category) && <option value={data.tattoo_category}>{data.tattoo_category}</option>}{categories.map(category => <option key={category} value={category}>{category}</option>)}</select></Field>
          </section>
          </div>
          <div className="ed-column">
        {selectedAppointment?.id && <details open key={selectedAppointment.id} className="ed-images ed-section">
          <summary className="ed-disclosure"><FiImage /><span><strong>Projektbilder</strong><small>Bilder ansehen oder hinzufügen</small></span><FiChevronDown /></summary>
          <div className="ed-disclosure-content"><AppointmentImageManager
          appointmentId={selectedAppointment.id} initialImages={data.images || []}
          legacyImageUrl={data.consultation_image_url} onOpenImages={onOpenImages}
          onImagesChange={nextImages => { setAppointmentData(previous => ({ ...previous, images: nextImages })); onImagesChanged?.(); }}
        /></div></details>}
          <section className="ed-section ed-costs">
            <button type="button" className="ed-disclosure" aria-expanded={pricesOpen} aria-controls="ed-private-prices" onClick={() => setPricesOpen(previous => !previous)}><FiLock /><span><strong>Preis & Anzahlung</strong><small>{pricesOpen ? 'Beträge wieder verbergen' : 'Antippen zum Anzeigen'}</small></span><FiChevronDown /></button>
            {pricesOpen && <div id="ed-private-prices" className="ed-disclosure-content">
            <div className="ed-pair">{[['minRange', 'Preis ab · €'], ['maxRange', 'Preis bis · €']].map(([key, label]) => <Field key={key} label={label}><input disabled={busy} type="text" inputMode="decimal" value={value(key)} onChange={e => update(key, e.target.value)} placeholder="0" /></Field>)}</div>
            <Field label="Anzahlung · €"><input disabled={busy} type="text" inputMode="decimal" value={value('deposit')} onChange={e => update('deposit', e.target.value)} placeholder="0" /></Field>
            </div>}
          </section>
          </div>
        </div>

      </div>
      {saveError && <div role="alert" style={{ padding: '12px 16px', color: '#ffb4b4', background: '#351e1e', flexShrink: 0 }}>{saveError}</div>}
      <footer className="ed-footer">
        <div className="ed-more">
          <button type="button" className="ed-icon" aria-label="Weitere Terminaktionen" aria-expanded={actionsOpen} disabled={busy} onClick={() => setActionsOpen(previous => !previous)}><FiMoreHorizontal /></button>
          {actionsOpen && <div className="ed-action-popover"><button type="button" disabled={busy || !selectedAppointment} onClick={() => { if (selectedAppointment) { setActionsOpen(false); onDeleteAppointment(selectedAppointment.id, selectedAppointment.dayId); } }}><FiTrash2 />Termin löschen</button></div>}
        </div>
        <button type="button" className="ed-cancel" onClick={close} disabled={busy}>Schließen</button>
        <button type="submit" className="ed-save" disabled={busy || !dirty}><FiCheck />{busy ? 'Speichert …' : dirty ? 'Speichern' : 'Gespeichert'}</button>
      </footer>
    </form>
  </Modal>;
}
